export const ROLE_OPTIONS = [
  'Expert assistant', 'Autonomous workflow operator', 'Research analyst', 'Product Manager',
  'Writer / Editor', 'Marketing specialist', 'Engineer', 'Business strategist',
  'Operations specialist', 'Sales professional', 'Customer success manager',
  'Teacher / Explainer', 'Legal reviewer', 'Coach / Mentor',
];

export const OUTPUT_OPTIONS = [
  'Answer / explanation', 'Summary', 'Email or message', 'Outline / bullets',
  'Document', 'Long-form artifact', 'Checklist', 'Recommendation', 'Comparison',
  'Step-by-step workflow', 'Integrated workflow instructions', 'Autonomous runbook', 'Code or script',
];

export const STYLE_OPTIONS = [
  'Clear and professional', 'Concise', 'Detailed', 'Friendly', 'Direct',
  'Analytical', 'Executive-level', 'Beginner-friendly', 'Instructional',
  'Structured and precise', 'Action-oriented', 'Conversational', 'Consultative',
];

export const USAGE_SURFACE_OPTIONS = [
  'PromptCrew',
  'ChatGPT',
  'Claude',
  'Gemini',
  'Microsoft Copilot',
  'Codex',
  'Cursor',
  'Kiro',
  'Claude Code',
  'VS Code',
  'Claude Cowork',
  'Slack',
  'Zapier',
  'Make',
  'n8n',
  'Pipedream',
  'AWS Lambda',
];

export const TYPE_FIELD_OPTIONS = {
  prompt: {
    role: ROLE_OPTIONS.filter((option) => option !== 'Autonomous workflow operator'),
    output: OUTPUT_OPTIONS.filter((option) => !['Integrated workflow instructions', 'Autonomous runbook'].includes(option)),
    usage_surface: ['PromptCrew', 'ChatGPT', 'Claude', 'Claude Code', 'Claude Cowork', 'Gemini', 'Microsoft Copilot'],
    defaults: {
      role: 'Expert assistant',
      output: 'Answer / explanation',
      usage_surface: 'PromptCrew',
    },
  },
  skill: {
    role: ['Workflow specialist', 'Operations coordinator', 'Research analyst', 'Sales professional', 'Customer success manager', 'Engineer', 'Product Manager'],
    output: ['Completed task', 'Generated file or document', 'Research brief', 'Updated record or workspace', 'Code change', 'Decision or handoff', 'Notification or message', 'Checklist or next steps'],
    style: ['Clear and professional', 'Structured and precise', 'Action-oriented', 'Thorough', 'Concise', 'Collaborative', 'Safe and conservative'],
    usage_surface: ['Claude', 'ChatGPT', 'Gemini', 'Microsoft Copilot', 'Slack', 'Codex', 'Cursor', 'Kiro', 'Claude Code', 'Claude Cowork', 'VS Code', 'Zapier', 'Make', 'n8n', 'Pipedream', 'AWS Lambda'],
    defaults: {
      role: 'Workflow specialist',
      output: 'Completed task',
      style: 'Structured and precise',
      usage_surface: '',
    },
  },
  agent: {
    role: ['Workflow operator', 'Monitoring agent', 'Operations coordinator', 'Research analyst', 'Engineering agent', 'Customer success manager'],
    output: ['Scheduled digest', 'Alert or escalation', 'Status report', 'Updated record or workspace', 'Generated file or document', 'Queue or task update', 'Exception report', 'Completed task'],
    style: ['Reliable and conservative', 'Structured and precise', 'Concise status updates', 'Action-oriented', 'Escalate early', 'Thorough'],
    usage_surface: ['Claude', 'ChatGPT', 'Gemini', 'Microsoft Copilot', 'Slack', 'Codex', 'Cursor', 'Kiro', 'Claude Code', 'Claude Cowork', 'VS Code', 'Zapier', 'Make', 'n8n', 'Pipedream', 'AWS Lambda'],
    defaults: {
      role: 'Workflow operator',
      output: 'Scheduled digest',
      style: 'Reliable and conservative',
      usage_surface: '',
    },
  },
};

const USAGE_SURFACE_ALIASES = {
  'promptcrew only': 'PromptCrew',
  'promptcrew chat': 'PromptCrew',
  'promptcrew creator': 'PromptCrew',
  promptcrew: 'PromptCrew',
  'prompt crew': 'PromptCrew',
  chatgpt: 'ChatGPT',
  'chat gpt': 'ChatGPT',
  claude: 'Claude',
  gemini: 'Gemini',
  cursor: 'Cursor',
  kiro: 'Kiro',
  'claude code': 'Claude Code',
  'claude cowork': 'Claude Cowork',
  codex: 'Codex',
  'microsoft copilot': 'Microsoft Copilot',
  vscode: 'VS Code',
  'vs code': 'VS Code',
};

export function normalizeCapabilityTypeValue(value) {
  return ['prompt', 'skill', 'agent'].includes(value) ? value : 'prompt';
}

export function normalizeUsageSurfaceValue(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return USAGE_SURFACE_ALIASES[raw.toLowerCase()] || raw;
}

export function getOptionsForCapabilityType(capabilityType, fieldKey) {
  const type = normalizeCapabilityTypeValue(capabilityType);
  return TYPE_FIELD_OPTIONS[type]?.[fieldKey] || (
    fieldKey === 'role' ? ROLE_OPTIONS
      : fieldKey === 'output' ? OUTPUT_OPTIONS
        : fieldKey === 'style' ? STYLE_OPTIONS
          : fieldKey === 'usage_surface' ? USAGE_SURFACE_OPTIONS
            : []
  );
}

export function getDefaultForCapabilityType(capabilityType, fieldKey) {
  const type = normalizeCapabilityTypeValue(capabilityType);
  return TYPE_FIELD_OPTIONS[type]?.defaults?.[fieldKey] || '';
}

export function coerceCreateFlowDataForType(data = {}, changedField = '') {
  const capabilityType = normalizeCapabilityTypeValue(data.capabilityType || 'prompt');
  const next = { ...data, capabilityType };
  if (next.usage_surface) {
    next.usage_surface = normalizeUsageSurfaceValue(next.usage_surface);
  }
  ['role', 'output', 'style', 'usage_surface'].forEach((fieldKey) => {
    const options = getOptionsForCapabilityType(capabilityType, fieldKey);
    const value = String(next[fieldKey] || '').trim();
    if (changedField === 'capabilityType') {
      // A runtime belongs to the selected capability type. Never carry a chat
      // surface such as PromptCrew into an Integrated or Autonomous workflow.
      next[fieldKey] = getDefaultForCapabilityType(capabilityType, fieldKey);
    } else if (value && !options.includes(value)) {
      next[fieldKey] = fieldKey === 'usage_surface'
        ? ''
        : getDefaultForCapabilityType(capabilityType, fieldKey) || value;
    }
  });
  return next;
}

export function buildCreateFlowSteps(capabilityType = 'prompt') {
  const type = normalizeCapabilityTypeValue(capabilityType);
  const usageQuestion = type === 'skill'
      ? '**Where will this integrated capability be used?**\n\nChoose the tool, IDE, coding assistant, or platform it should use so I can include integration details, access assumptions, and handoff steps.'
      : '**Where will this autonomous capability run?**\n\nChoose the runtime, automation platform, or MCP-enabled setup it should target so I can include triggers, schedule, permissions, approval points, and stop conditions.';
  return [
    { key: 'goal', question: 'What are you trying to accomplish with this capability?', chips: null },
    { key: 'role', question: type === 'prompt' ? 'What role should the AI take? Pick one below, or type your own.' : 'What operating role best fits this workflow? Pick one below, or type your own.', chips: getOptionsForCapabilityType(type, 'role') },
    { key: 'output', question: type === 'prompt' ? 'What output type should this produce? Pick one below, or type your own.' : 'What should be complete when this runs? Pick one below, or type your own.', chips: getOptionsForCapabilityType(type, 'output') },
    { key: 'style', question: type === 'prompt' ? 'What style or tone? Pick one below, or type your own.' : 'How should this workflow operate? Pick one below, or type your own.', chips: getOptionsForCapabilityType(type, 'style') },
    ...(type === 'prompt' ? [] : [{
      key: 'usage_surface',
      question: usageQuestion,
      chips: getOptionsForCapabilityType(type, 'usage_surface'),
    }]),
    { key: 'rules', question: 'Any key requirements or constraints? (optional)', chips: null },
  ];
}

export const CREATE_FLOW_STEPS = buildCreateFlowSteps('prompt');

export const EMPTY_CREATE_FLOW_DATA = {
  goal: '',
  role: '',
  output: '',
  style: '',
  rules: '',
  usage_surface: '',
  capabilityType: 'prompt',
  inputs: '',
  dependencies: '',
  successCriteria: '',
  trigger: '',
  approvalBoundary: '',
};

export const CORE_STEP_KEYS = ['role', 'output', 'style', 'usage_surface'];

/** Fields required on the summary card before generate (includes type). */
export const SUMMARY_SETTING_KEYS = ['capabilityType'];
